import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import {
    getCachedSettingsParams,
    hasSettingsParamsCache,
} from 'utils/helpers/settings/settingsParamsCache';
import conf from 'conf/conf.js';
import {
    getBaseCurrencyPrefix,
    getIsLocalCurrencyMode,
    getLocalCurrencyPrefix,
    getPrimaryCurrencyPrefix,
} from 'utils/helpers/currency/currencyHelpers';

export const getIsUahMode = (rate: number) => getIsLocalCurrencyMode(rate);

export const getUseUsd = (pricesInUsd: boolean | unknown, rate: number) =>
    !getIsUahMode(rate) && Boolean(pricesInUsd);

export const getFmtRate = (rate: number) => (Number.isFinite(rate) && rate > 0 ? rate : 1);

/** Company Settings vatPercent; falls back to env REACT_APP_VAT / 20 before settings load. */
export const getVatPercent = () => {
    if (hasSettingsParamsCache()) {
        const fromSettings = Number(getCachedSettingsParams().vatPercent);
        if (Number.isFinite(fromSettings) && fromSettings >= 0) return fromSettings;
    }
    const value = Number(conf.vat);
    return Number.isFinite(value) && value >= 0 ? value : 20;
};

export const getVatRate = () => getVatPercent() / 100;

export const calcVatAmount = (amount: number, withVat: boolean | unknown = false) => {
    if (!withVat) return 0;
    return safeFloatParse((safeFloatParse(amount) || 0) * getVatRate()) || 0;
};

export const calcAmountWithVat = (amount: number, withVat: boolean | unknown = false) => {
    const base = safeFloatParse(amount) || 0;
    if (!withVat) return base;
    return safeFloatParse(base + calcVatAmount(base, true)) || 0;
};

export const getSaleUsd = (row: {
    priceSale?: number;
    price?: number;
}) => safeFloatParse(row.priceSale ?? row.price ?? 0) || 0;

export const calcSaleRate = (saleUsd: number, rate: number) =>
    safeFloatParse(saleUsd * getFmtRate(rate)) || 0;

export const calcUsdFromRate = (saleRate: number, rate: number) =>
    safeFloatParse(saleRate / getFmtRate(rate)) || 0;

export const saleUsdFromEditValue = (value: string | number, rate: number, editRatePrice: boolean) => {
    const amount = safeFloatParse(value || 0) || 0;
    return editRatePrice ? calcUsdFromRate(amount, rate) : amount;
};

export const getPriceEditDefaultValue = (
    row: { priceSale?: number; price?: number },
    rate: number,
    editRatePrice: boolean,
) => {
    const saleUsd = getSaleUsd(row);
    return editRatePrice ? calcSaleRate(saleUsd, rate) : saleUsd;
};

export const getPriceEditPrefix = (rate: number, editRatePrice: boolean) =>
    editRatePrice ? getLocalCurrencyPrefix() : getPrimaryCurrencyPrefix(rate);

export const getSaleRate = (
    row: {
        priceSale?: number;
        price?: number;
        priceSaleRate?: number;
        priceRate?: number;
    },
    rate: number,
) => calcSaleRate(getSaleUsd(row), rate);

export const getDisplayPrice = (
    row: {
        priceSale?: number;
        price?: number;
    },
    rate: number,
    pricesInUsd: boolean | unknown,
) => {
    const saleUsd = getSaleUsd(row);
    return getUseUsd(pricesInUsd, rate) ? saleUsd : calcSaleRate(saleUsd, rate);
};

export const formatPrimaryPrice = (amount: number, rate: number) => {
    const prefix = getIsUahMode(rate) ? getLocalCurrencyPrefix() : getBaseCurrencyPrefix();
    return `${prefix}${formatPrice(amount)}`;
};

export const shouldShowRateLine = (rate: number) => !getIsUahMode(rate);

export const formatRateLine = (saleUsd: number, rate: number) =>
    `${getLocalCurrencyPrefix()}${formatPrice(calcSaleRate(saleUsd, rate))}`;

export const getLineTotal = (
    row: { itemCount?: number; priceSale?: number; price?: number },
    rate: number,
    pricesInUsd: boolean | unknown = false,
) => {
    const itemCount = safeFloatParse(row.itemCount ?? 0) || 0;
    return safeFloatParse(itemCount * getDisplayPrice(row, rate, pricesInUsd)) || 0;
};

export const getLineVat = (
    row: { itemCount?: number; priceSale?: number; price?: number },
    rate: number,
    pricesInUsd: boolean | unknown = false,
    withVat: boolean | unknown = false,
) => calcVatAmount(getLineTotal(row, rate, pricesInUsd), withVat);

export const getLineTotalWithVat = (
    row: { itemCount?: number; priceSale?: number; price?: number },
    rate: number,
    pricesInUsd: boolean | unknown = false,
    withVat: boolean | unknown = false,
) => calcAmountWithVat(getLineTotal(row, rate, pricesInUsd), withVat);

export const buildSalePatch = (saleUsd: number, rate: number) => {
    const priceSale = safeFloatParse(saleUsd || 0) || 0;
    return {
        priceSale,
        priceSaleRate: calcSaleRate(priceSale, rate),
    };
};

/** Cost + sale fields from catalog defaults (same shape as when adding a row). */
export const buildCatalogPricePatch = (costUsd: number, saleUsd: number, rate: number) => {
    const price = safeFloatParse(costUsd || 0) || 0;
    const priceSale = safeFloatParse(saleUsd || price) || 0;
    const fmtRate = getFmtRate(rate);
    return {
        price,
        priceRate: safeFloatParse(price * fmtRate) || 0,
        priceSale,
        priceSaleRate: safeFloatParse(priceSale * fmtRate) || 0,
    };
};

export const formatTotal = (total: number, rate: number, pricesInUsd: boolean | unknown = false) => {
    const prefix = getUseUsd(pricesInUsd, rate)
        ? getBaseCurrencyPrefix()
        : getLocalCurrencyPrefix();
    return `${prefix}${formatPrice(total)}`;
};

export const getTotalAmount = (
    products: Array<{ itemCount?: number; priceSale?: number; price?: number }>,
    additionalItems: Array<{ itemCount?: number; priceSale?: number; price?: number }>,
    pricesInUsd: boolean | unknown,
    rate: number,
    withVat: boolean | unknown = false,
) => {
    const productsTotal = (products || []).reduce(
        (sum, row) => sum + getLineTotal(row, rate, pricesInUsd),
        0,
    );
    const additionalTotal = (additionalItems || []).reduce(
        (sum, row) => sum + getLineTotal(row, rate, pricesInUsd),
        0,
    );
    return calcAmountWithVat(safeFloatParse(productsTotal + additionalTotal) || 0, withVat);
};

export const getTotalVatAmount = (
    products: Array<{ itemCount?: number; priceSale?: number; price?: number }>,
    additionalItems: Array<{ itemCount?: number; priceSale?: number; price?: number }>,
    pricesInUsd: boolean | unknown,
    rate: number,
    withVat: boolean | unknown = false,
) => {
    if (!withVat) return 0;
    const productsTotal = (products || []).reduce(
        (sum, row) => sum + getLineTotal(row, rate, pricesInUsd),
        0,
    );
    const additionalTotal = (additionalItems || []).reduce(
        (sum, row) => sum + getLineTotal(row, rate, pricesInUsd),
        0,
    );
    return calcVatAmount(safeFloatParse(productsTotal + additionalTotal) || 0, true);
};
